import type { TokenRefreshConfig } from '../types/config';
import { DEFAULT_TOKEN_REFRESH_CONFIG } from './defaults';

/**
 * Merge user-provided config with default configuration.
 *
 * Performs a deep merge to ensure all nested properties are properly combined.
 * User config takes precedence over defaults.
 *
 * @param userConfig Optional user-provided configuration
 * @returns Complete TokenRefreshConfig with all required properties
 *
 * @example
 * ```typescript
 * const config = mergeConfig({
 *   retryStrategies: {
 *     coldStart: {
 *       maxAttempts: 10, // Override default
 *     }
 *   }
 * });
 * // Returns full config with user's coldStart.maxAttempts and all other defaults
 * ```
 */
export function mergeConfig(
  userConfig?: Partial<TokenRefreshConfig>
): TokenRefreshConfig {
  return {
    ...DEFAULT_TOKEN_REFRESH_CONFIG,
    ...userConfig,
    retryStrategies: {
      ...DEFAULT_TOKEN_REFRESH_CONFIG.retryStrategies,
      ...userConfig?.retryStrategies,
    },
    timeouts: {
      ...DEFAULT_TOKEN_REFRESH_CONFIG.timeouts,
      ...userConfig?.timeouts,
    },
    telemetry: {
      enabled:
        userConfig?.telemetry?.enabled ??
        DEFAULT_TOKEN_REFRESH_CONFIG.telemetry?.enabled ??
        false,
      onRetryAttempt:
        userConfig?.telemetry?.onRetryAttempt ??
        DEFAULT_TOKEN_REFRESH_CONFIG.telemetry?.onRetryAttempt,
      onRetrySuccess:
        userConfig?.telemetry?.onRetrySuccess ??
        DEFAULT_TOKEN_REFRESH_CONFIG.telemetry?.onRetrySuccess,
      onRetryExhausted:
        userConfig?.telemetry?.onRetryExhausted ??
        DEFAULT_TOKEN_REFRESH_CONFIG.telemetry?.onRetryExhausted,
    },
  };
}
